import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { useClickOutside } from '../hooks/useClickOutside';
import SuggestionList from './SuggestionList';
import SearchInput from './SearchInput';
import '../global-sass/main.scss';

const AutocompleteSearch = ({
	suggestions,
	inputString,
	setInputString,
	setSelectedPlaceId
}) => {
	const labels = useLabels();
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const wrapperRef = useRef(null);

	// Close dropdown when clicking/focusing outside
	useClickOutside(wrapperRef, () => {
		setShowSuggestions(false);
		setHighlightedIndex(-1);
	});

	// When suggestions open, highlight first item
	useEffect(() => {
		if (showSuggestions && suggestions && suggestions.length > 0) {
			setHighlightedIndex(0);
		} else {
			setHighlightedIndex(-1);
		}
	}, [showSuggestions, suggestions]);

	const handleInputChange = (event) => {
		const { value } = event.target;
		setInputString(value);
		if (value.trim().length >= 3) {
			setShowSuggestions(true);
		}
	};

	const handleSelectSuggestion = useCallback(
		(suggestion) => {
			const { text: suggestionText, placeId } =
				suggestion.placePrediction;
			setInputString(suggestionText.text);
			setShowSuggestions(false);
			setSelectedPlaceId(placeId);
		},
		[setInputString, setSelectedPlaceId]
	);

	const clearInput = () => {
		setInputString('');
		setShowSuggestions(false);
		setSelectedPlaceId(null);
		setHighlightedIndex(-1);
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		setShowSuggestions(false);
	};

	const handleKeyDown = useCallback(
		(e) => {
			if (!showSuggestions || !suggestions || suggestions.length === 0)
				return;
			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setHighlightedIndex(
						(prev) => (prev + 1) % suggestions.length
					);
					break;
				case 'ArrowUp':
					e.preventDefault();
					setHighlightedIndex(
						(prev) =>
							(prev - 1 + suggestions.length) % suggestions.length
					);
					break;
				case 'Enter':
					e.preventDefault();
					if (highlightedIndex >= 0) {
						handleSelectSuggestion(suggestions[highlightedIndex]);
					}
					break;
				case 'Escape':
					e.preventDefault();
					setShowSuggestions(false);
					setHighlightedIndex(-1);
					break;
				default:
					break;
			}
		},
		[showSuggestions, suggestions, highlightedIndex, handleSelectSuggestion]
	);

	const handleInputFocus = () => {
		if (inputString && inputString.trim().length >= 3) {
			setShowSuggestions(true);
		}
	};

	return (
		<form
			onSubmit={handleSubmit}
			name="location-autocomplete-search"
			className="center-block autocomplete-search-form"
			ref={wrapperRef}
		>
			<strong className="text-muted">
				{labels.get('SEARCH_BY_LOCATION')}
			</strong>
			<div
				role="combobox"
				className="form-group position-relative"
				aria-expanded={showSuggestions}
				aria-haspopup="listbox"
				aria-controls="location-suggestion-list"
			>
				<SearchInput
					value={inputString}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onKeyDown={handleKeyDown}
					onClear={clearInput}
				/>
				<SuggestionList
					suggestions={suggestions}
					isVisible={showSuggestions}
					suggestionsLabel={labels.get('SUGGESTIONS')}
					onSelect={(index) =>
						handleSelectSuggestion(suggestions[index])
					}
					highlightedIndex={highlightedIndex}
					onMouseEnter={(index) => setHighlightedIndex(index)}
					onMouseLeave={() => setHighlightedIndex(-1)}
				/>
			</div>
		</form>
	);
};

AutocompleteSearch.propTypes = {
	suggestions: PropTypes.arrayOf(PropTypes.shape({})),
	inputString: PropTypes.string,
	setInputString: PropTypes.func,
	setSelectedPlaceId: PropTypes.func
};

export default AutocompleteSearch;
